import {
  Business,
  HomeWork,
  ImportantDevices,
  LocationCity,
  MarkunreadMailboxOutlined,
  PeopleAltOutlined,
  ViewQuilt,
  Work,
} from '@icons';
import { BROKER_SITE_ROLES, PORTAL_SITE_ROLES, UserRole } from 'enums';
import { messages } from 'i18n';
import { RouteDefinitionMap } from 'types';
import { BROKER_NESTED_ROUTES, BROKER_ROUTES } from './broker-routes';
import SubmissionAutonomousCreationModal from './dialogs/SubmissionAutonomousCreation';
import AgenciesPage from './pages/AgenciesPage';
import BackOfficePage from './pages/BackOfficePage';
import CarriersPage from './pages/CarriersPage';
import HomePage from './pages/HomePage';
import MarketsPage from './pages/MarketsPage';
import MyTeamPage from './pages/MyTeamPage';
import PlacementServicesPage from './pages/PlacementServicesPage';
import RetailerSubmissionsPage from './pages/RetailerSubmissionsPage';
import SubmissionQueuePage from './pages/SubmissionQueuePage';
import SubmissionsBoxPage from './pages/SubmissionsBoxPage';
import SubmissionServicesPage from './pages/SubmissionServicesPage';
import SubmissionWorkspacePage, {
  CompareQuotesModal,
  MarketingReport,
  PortalWorkspacePage,
  QuoteReview,
} from './pages/SubmissionWorkspacePage';

import TemplatesPage from './pages/TemplatesPage';
import Templates from './pages/TemplatesPage/Templates';

const brokerSiteRoles = [...BROKER_SITE_ROLES, UserRole.BackOffice];
const portalSiteRoles = [...PORTAL_SITE_ROLES, UserRole.BackOffice];

const brokerRoutes: RouteDefinitionMap = {
  [`/${BROKER_ROUTES.BACK_OFFICE}`]: {
    Component: BackOfficePage,
    roles: [UserRole.BackOffice],
    menuItem: {
      Icon: ImportantDevices,
      label: messages.menu.backOffice,
      divider: true,
      path: `/${BROKER_ROUTES.BACK_OFFICE}`,
    },
  },
  [`/${BROKER_ROUTES.SUBMISSION_BOX}`]: {
    Component: SubmissionsBoxPage,
    roles: brokerSiteRoles,
    menuItem: {
      Icon: MarkunreadMailboxOutlined,
      label: messages.menu.submissionBox,
      divider: true,
      path: `/${BROKER_ROUTES.SUBMISSION_BOX}`,
    },
  },
  [`/${BROKER_ROUTES.SUBMISSION_SERVICES}`]: {
    Component: SubmissionServicesPage,
    roles: [UserRole.OpEx],
    menuItem: {
      Icon: PeopleAltOutlined,
      label: messages.menu.submissionServices,
      divider: true,
      path: `/${BROKER_ROUTES.SUBMISSION_SERVICES}`,
    },
  },
  [`/${BROKER_ROUTES.SUBMISSION_QUEUE}`]: {
    Component: SubmissionQueuePage,
    roles: [UserRole.OpEx],
    menuItem: {
      Icon: HomeWork,
      label: messages.menu.submissionQueue,
      path: `/${BROKER_ROUTES.SUBMISSION_QUEUE}`,
    },
  },
  [`/${BROKER_ROUTES.PORTAL}`]: {
    Component: RetailerSubmissionsPage,
    roles: portalSiteRoles,
  },
  [`/${BROKER_ROUTES.HOME}`]: {
    Component: HomePage,
    roles: brokerSiteRoles,
    menuItem: {
      Icon: HomeWork,
      label: messages.menu.submissions,
      divider: true,
      path: `/${BROKER_ROUTES.HOME}`,
    },
    isHomePage: true,
    nestedRoute: {
      [`${BROKER_NESTED_ROUTES.HOME.SUBMISSION_AUTONOMOUS_CREATION}/*`]: {
        Component: SubmissionAutonomousCreationModal,
        roles: brokerSiteRoles,
      },
    },
  },
  [`/${BROKER_ROUTES.MARKETS}/*`]: {
    Component: MarketsPage,
    roles: brokerSiteRoles,
    menuItem: {
      Icon: Work,
      label: messages.menu.markets,
      path: `/${BROKER_ROUTES.MARKETS}`,
    },
  },
  [`/${BROKER_ROUTES.CARRIERS}/*`]: {
    Component: CarriersPage,
    roles: [UserRole.OpEx],
    menuItem: {
      Icon: Business,
      label: messages.menu.carriers,
      path: `/${BROKER_ROUTES.CARRIERS}`,
      forceHide: (loggedInUser) => loggedInUser?.endUser?.role === UserRole.Retailer,
    },
  },
  [`/${BROKER_ROUTES.TEMPLATES}`]: {
    Component: TemplatesPage,
    roles: brokerSiteRoles,
    menuItem: {
      Icon: ViewQuilt,
      label: messages.menu.templates,
      path: `/${BROKER_ROUTES.TEMPLATES}`,
    },
    nestedRoute: {
      ':templateId': {
        Component: Templates,
        roles: brokerSiteRoles,
      },
    },
  },
  [`/${BROKER_ROUTES.PLACEMENT_SERVICES}`]: {
    Component: PlacementServicesPage,
    roles: [UserRole.CapitolaBroker],
    menuItem: {
      Icon: PeopleAltOutlined,
      label: messages.menu.placementServices,
      divider: true,
      path: `/${BROKER_ROUTES.PLACEMENT_SERVICES}`,
    },
  },
  [`/${BROKER_ROUTES.TEAM}/*`]: {
    Component: MyTeamPage,
    roles: [...brokerSiteRoles, UserRole.OpEx],
    menuItem: {
      Icon: PeopleAltOutlined,
      label: messages.menu.myTeam,
      path: `/${BROKER_ROUTES.TEAM}`,
    },
  },
  [`/${BROKER_ROUTES.AGENCIES}/*`]: {
    Component: AgenciesPage,
    roles: brokerSiteRoles,
    menuItem: {
      Icon: LocationCity,
      label: messages.menu.agencies,
      path: `/${BROKER_ROUTES.AGENCIES}`,
      forceHide: (loggedInUser) => loggedInUser?.endUser?.role === UserRole.Retailer,
    },
  },
  [`/${BROKER_ROUTES.SUBMISSION}/*`]: {
    Component: SubmissionWorkspacePage,
    nestedRoute: {
      [`:tab/${BROKER_NESTED_ROUTES.WORKSPACE.COMPARE_QUOTES}/*`]: {
        Component: CompareQuotesModal,
        roles: brokerSiteRoles,
      },
      [`:tab/${BROKER_NESTED_ROUTES.WORKSPACE.SUBMISSION_AUTONOMOUS_CREATION}/*`]: {
        Component: SubmissionAutonomousCreationModal,
        roles: brokerSiteRoles,
      },
    },
    roles: brokerSiteRoles,
  },
  [`/${BROKER_ROUTES.RETAILER_SUBMISSION}/*`]: {
    Component: PortalWorkspacePage,
    nestedRoute: {
      [`${BROKER_NESTED_ROUTES.WORKSPACE.MARKETING_REPORT}/*`]: {
        Component: MarketingReport,
        roles: portalSiteRoles,
      },
      [`${BROKER_NESTED_ROUTES.WORKSPACE.QUOTE}/*`]: {
        Component: QuoteReview,
        roles: portalSiteRoles,
      },
    },
    roles: portalSiteRoles,
  },
};

export default brokerRoutes;
